import { Fragment, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { IconArrowLeft, IconCheck, IconExternalLink, IconPlus, IconTrash } from '@tabler/icons-react';

import { CALENDAR_ITEM_STATUS } from 'common/constants';
import { Button, Textarea, Loader, TableRowActions, CalendarItemStatusLabel, IconButton } from 'ui';

import { getUpdates, createUpdate, getNewsSuggestions } from '../api';
import SuggestTopicModal from '../components/updates/SuggestTopicModal';

const UPDATE_QUESTIONS = [
  'What new significant milestones or goals did you achieve this month?',
  'What challenge is the organization facing that donors could help with?',
  'What initiatives coming up this month that you want to highlight?',
  'What new numbers or statistics do you have to share about your work?',
  'What new personal stories do you have to share?',
];

const Updates = () => {
  const location = useLocation();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(location.state?.showCreateForm || false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState(UPDATE_QUESTIONS.map(() => ''));
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [newsSuggestions, setNewsSuggestions] = useState([]);
  const [loadingNewsSuggestions, setLoadingNewsSuggestions] = useState(false);

  const fetchUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUpdates();
      setUpdates(data?.filter((update) => update.status === 'submitted') || []);
    } catch (error) {
      console.error('Failed to fetch updates:', error);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  useEffect(() => {
    if (showCreateForm && newsSuggestions.length === 0 && !loadingNewsSuggestions) {
      fetchNewsSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateForm]);

  const handleAnswerChange = (index, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddSuggestion = ({ topic, details }) => {
    setCustomerSuggestions((prev) => [...prev, { topic, details }]);
  };

  const handleRemoveSuggestion = (index) => {
    setCustomerSuggestions((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchNewsSuggestions = useCallback(async () => {
    try {
      setLoadingNewsSuggestions(true);
      const suggestions = await getNewsSuggestions();
      setNewsSuggestions((suggestions || []).map((s) => ({ ...s, approved: false, customerNote: '' })));
    } catch (error) {
      console.error('Failed to fetch news suggestions:', error);
      setNewsSuggestions([]);
    } finally {
      setLoadingNewsSuggestions(false);
    }
  }, []);

  const handleSubmit = async () => {
    const filledAnswers = UPDATE_QUESTIONS.map((question, i) => ({
      question,
      answer: answers[i].trim(),
    })).filter((a) => a.answer);

    if (filledAnswers.length === 0 && customerSuggestions.length === 0) {
      return;
    }

    try {
      setSubmitting(true);
      await createUpdate({
        answers: filledAnswers,
        customerSuggestions,
        newsSuggestions: newsSuggestions,
      });
      setShowCreateForm(false);
      setAnswers(UPDATE_QUESTIONS.map(() => ''));
      setCustomerSuggestions([]);
      setNewsSuggestions([]);
      await fetchUpdates();
    } catch (error) {
      console.error('Failed to create update:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(Number(timestamp) * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (showCreateForm) {
    return (
      <div className="w-full space-y-6">
        <div className="flex items-center space-x-1">
          <IconButton
            Icon={<IconArrowLeft size={20} />}
            color="transparent"
            className="-ml-2"
            onClick={() => setShowCreateForm(false)}
            srOnly="Cancel"
          />

          <h1 className="text-h3">Submit an Update</h1>
        </div>

        <div className="flex items-end justify-between rounded-lg border border-gray-100 bg-white-100 p-8">
          <div className="max-w-4xl space-y-4 text-lg">
            <p>Use this form to share recent updates about your organization or suggest ideas for future emails.</p>
            <p>
              The DonorSpring team will use this information to create new <strong>Suggested Drafts</strong> that
              support your existing <strong>Content Calendar</strong>. We will remind you to submit updates each month,
              but you are welcome to share updates anytime.
            </p>
            <p>
              Please be as specific as possible. We are looking for{' '}
              <strong>timely updates, announcements, or ideas</strong> that should be reflected in the next batch of
              Suggested Drafts we prepare for you.
            </p>
            <p>
              If the information is something that should stay visible long term, please also add it to your
              <strong> Organization Profile</strong> so it can guide future content as well.
            </p>
          </div>
          <div className="flex flex-col items-end">
            <Button
              title="Submit Update"
              color="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={
                submitting ||
                (!answers.some((a) => a.trim()) &&
                  customerSuggestions.length === 0 &&
                  newsSuggestions.filter((s) => s.approved).length === 0)
              }
            />
          </div>
        </div>

        <div className="space-y-6 rounded-xl bg-white-100 p-8">
          {UPDATE_QUESTIONS.map((question, index) => (
            <Textarea
              key={index}
              id={`question-${index}`}
              label={question}
              value={answers[index]}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              inputClassName="field-sizing-content min-h-24 max-w-4xl"
            />
          ))}
        </div>

        {loadingNewsSuggestions && (
          <div className="relative flex items-center justify-center py-8">
            <Loader />
          </div>
        )}

        {!loadingNewsSuggestions && newsSuggestions.length > 0 && (
          <div className="space-y-4 rounded-xl bg-white-100 px-4 py-6">
            <div className="px-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold">Suggested Email Topics</h2>
                <div className="w-fit rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-orange-700">
                  <p className="text-caption1 font-semibold uppercase">Beta</p>
                </div>
              </div>
              <p className="max-w-2xl text-sm text-gray-500">
                We&apos;ve compiled a list of potential email topics based on recent news that may be related to your
                organization&apos;s mission. Any topics you approve will be used to create new Suggested Drafts.
              </p>
            </div>

            {newsSuggestions.length > 0 && (
              <div className="relative overflow-x-auto sm:rounded-lg">
                <table className="w-full text-left text-base text-gray-950">
                  <thead className="bg-white-100 text-sm uppercase text-gray-400">
                    <tr>
                      <th className="px-4 py-3">Topic</th>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {newsSuggestions.map((suggestion, index) => (
                      <Fragment key={index}>
                        <tr className="border-b border-gray-50 bg-white-100 hover:bg-gray-10">
                          <td className="px-4 py-3">
                            <p className="text-base">{suggestion.title}</p>
                            {suggestion.description && (
                              <p className="text-sm text-gray-600">{suggestion.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {suggestion.sourceUrl && (
                              <Button
                                title={new URL(suggestion.sourceUrl).hostname.replace('www.', '')}
                                href={suggestion.sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                                LeftIcon={IconExternalLink}
                                size="sm"
                                className="max-w-48 whitespace-nowrap"
                                as="link"
                                titleClassName="overflow-hidden text-ellipsis"
                              />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <CalendarItemStatusLabel
                              status={
                                suggestion.approved ? CALENDAR_ITEM_STATUS.approved : CALENDAR_ITEM_STATUS.pendingReview
                              }
                            />
                          </td>
                          <td className="px-4 py-3">
                            <TableRowActions
                              actions={['Approve']}
                              onSelectAction={(action) => {
                                if (action === 'Approve') {
                                  setNewsSuggestions((prev) =>
                                    prev.map((s, i) => (i === index ? { ...s, approved: !s.approved } : s)),
                                  );
                                }
                              }}
                              getActionIcon={() => (
                                <IconCheck size={20} className={`${suggestion.approved ? 'text-green-500' : ''}`} />
                              )}
                              visibleActions={3}
                            />
                          </td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="space-y-6 rounded-xl bg-white-100 px-4 py-6">
          <div className="px-4">
            <h2 className="text-lg font-semibold">Additional Email Topics</h2>
            <p className="text-sm text-gray-500">Add additional email topics or campaign ideas.</p>
          </div>

          {customerSuggestions.length > 0 && (
            <div className="relative overflow-x-auto sm:rounded-lg">
              <table className="w-full text-left text-base text-gray-950">
                <thead className="bg-white-100 text-sm uppercase text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Topic</th>
                    <th className="w-[68px] px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {customerSuggestions.map((suggestion, index) => (
                    <tr key={index} className="border-b border-gray-50 bg-white-100 hover:bg-gray-10">
                      <td className="px-4 py-3">
                        <p className="text-base font-medium">{suggestion.topic}</p>
                        {suggestion.details && <p className="text-sm text-gray-600">{suggestion.details}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <IconButton
                          Icon={<IconTrash size={18} />}
                          color="transparent"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleRemoveSuggestion(index)}
                          srOnly="Remove suggestion"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-4">
            <Button
              onClick={() => setIsSuggestModalOpen(true)}
              color="primary"
              title="Suggest Additional Topic"
              LeftIcon={IconPlus}
            />
          </div>
        </div>

        <SuggestTopicModal
          open={isSuggestModalOpen}
          onClose={() => setIsSuggestModalOpen(false)}
          onSubmit={handleAddSuggestion}
        />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h3">Updates</h1>
        <Button LeftIcon={IconPlus} title="New Update" color="primary" onClick={() => setShowCreateForm(true)} />
      </div>

      {loading && (
        <div className="relative">
          <Loader />
        </div>
      )}

      {!loading && updates.length === 0 && (
        <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-gray-200 bg-white-100 py-12">
          <p className="text-gray-500">No updates submitted yet.</p>
          <Button title="Submit Your First Update" color="primary" onClick={() => setShowCreateForm(true)} />
        </div>
      )}

      {!loading && updates.length > 0 && (
        <div className="space-y-4">
          {updates.map((update) => (
            <div key={update.updateId} className="space-y-4 rounded-xl border border-gray-100 bg-white-100 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{formatDate(update.createdAt)}</h2>
              </div>

              {update.answers?.length > 0 && (
                <div className="space-y-3">
                  {update.answers.map((a, i) => (
                    <div key={i}>
                      <p className="text-sm font-medium text-gray-500">{a.question}</p>
                      <p className="text-base">{a.answer}</p>
                    </div>
                  ))}
                </div>
              )}

              {update.newsSuggestions?.filter((s) => s.approved)?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold uppercase text-gray-400">Approved Email Topics</h3>
                  <div className="space-y-1">
                    {update.newsSuggestions
                      .filter((s) => s.approved)
                      .map((s, i) => (
                        <div key={i} className="flex items-start space-x-2">
                          <div>
                            <p className="text-base">{s.title}</p>
                            {s.description && <p className="text-sm text-gray-500">{s.description}</p>}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {update.customerSuggestions?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold uppercase text-gray-400">Additional Email Topics</h3>
                  <div className="space-y-1">
                    {update.customerSuggestions.map((s, i) => (
                      <div key={i}>
                        <p className="text-base font-medium">{s.topic}</p>
                        {s.details && <p className="text-sm text-gray-500">{s.details}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Updates;
